// Central error reporter — augments Sentry with our own queryable Firestore
// mirror so we can triage, dedupe, and attach domain context (user role,
// isDesignPartner, screen, etc.) that Sentry doesn't have.
//
// PRIVACY: This module is for ops/debug only. Content captured here must NEVER
// feed into lead scoring, vendor-quality models, or any user-facing surface.
//
// Contract:
//   - reportError(err, ctx)        — fire-and-forget, ALWAYS swallows its own errors
//   - captureBreadcrumb(cat, msg)  — in-memory ring buffer, max 50 entries
//   - getBreadcrumbs()             — snapshot for inclusion in error events
//
// Loop safety: every code path here is wrapped in try/catch. The only fallback
// is console.error. Never call reportError from inside reportError.

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getDb, isFirebaseReady } from "../firebase/instance";
import { stripUndefined } from "../utils/stripUndefined";

// Optional sink for errorEvents WRITE failures. The write is fire-and-forget and
// must never crash the app, but it should not be invisible either: platforms
// register this at bootstrap (mobile → Sentry.captureException) so a broken
// write path — like the prod named-DB client write that silently dropped every
// mobile error report after the multi-DB split — actually surfaces.
let writeErrorSink: ((err: unknown) => void) | null = null;
export function setReportWriteErrorSink(fn: ((err: unknown) => void) | null): void {
  writeErrorSink = fn;
}

export type ErrorSurface = "mobile" | "web" | "functions";
export type ErrorSource =
  | "auto"
  | "boundary"
  | "global"
  | "user_report"
  | "manual";

export interface ErrorContext {
  surface: ErrorSurface;
  userId?: string;
  userRole?: string;
  isDesignPartner?: boolean;
  screen?: string;
  buildVersion?: string;
  source?: ErrorSource;
  // Active team-profile id (vendor sub-user) at the time of a user report.
  // The onErrorEventCreated trigger resolves this to the profile name to route
  // our internal QA sub-user's reports to the Bug Center "In House" tab and
  // suppress Telegram for them. Omitted for customers (no active profile).
  reporterProfileId?: string;
  // Additional fields are allowed for user reports (description, expected, etc.)
  // but must be explicitly enumerated by callers, not arbitrary `extra` dumps —
  // arbitrary extras risk leaking PII into the errorEvents collection.
  extra?: Record<string, string | number | boolean | null>;
  // User-report-only fields
  description?: string;
  expected?: string;
  actual?: string;
  severityHint?: "P0" | "P1" | "P2";
  screenshotPath?: string;
}

export interface Breadcrumb {
  category: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: number;
}

const MAX_BREADCRUMBS = 50;
const breadcrumbs: Breadcrumb[] = [];

export function captureBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  try {
    breadcrumbs.push({
      category,
      message,
      data,
      timestamp: Date.now(),
    });
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
      breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
    }
  } catch {
    // Swallow — breadcrumb capture must never throw.
  }
}

export function getBreadcrumbs(): Breadcrumb[] {
  return breadcrumbs.slice();
}

export function clearBreadcrumbs(): void {
  breadcrumbs.length = 0;
}

function safeStringify(err: unknown): { name: string; message: string; stack: string } {
  try {
    if (err instanceof Error) {
      return {
        name: err.name || "Error",
        message: String(err.message || "").slice(0, 2000),
        stack: String(err.stack || "").slice(0, 4000),
      };
    }
    if (typeof err === "string") {
      return { name: "StringError", message: err.slice(0, 2000), stack: "" };
    }
    return { name: "UnknownError", message: JSON.stringify(err).slice(0, 2000), stack: "" };
  } catch {
    return { name: "UnknownError", message: "Failed to stringify error", stack: "" };
  }
}

function topFrame(stack: string): string {
  // First non-empty line after the error message that looks like a frame.
  const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    if (line.startsWith("at ") || line.includes("@")) {
      return line.slice(0, 200);
    }
  }
  return lines[1] || lines[0] || "";
}

async function sha256Hex(input: string): Promise<string> {
  // Use Web Crypto where available (web, modern RN/Hermes). Falls back to a
  // tiny non-cryptographic hash so the reporter never throws in odd runtimes.
  try {
    const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
    if (subtle) {
      const bytes = new TextEncoder().encode(input);
      const digest = await subtle.digest("SHA-256", bytes);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch {
    // fall through to fallback
  }
  // Fallback: FNV-1a 32-bit hex. Not cryptographic, fine for grouping.
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

interface ReportOptions {
  // Force a specific fingerprint (e.g. for user reports, where the stack is
  // less meaningful than the screen + user description).
  fingerprintOverride?: string;
  // Pre-resolved environment string. Surfaces typically pass __DEV__ or
  // process.env.NODE_ENV themselves; we don't probe globals here.
  environment?: "development" | "production" | "emulator";
}

export async function reportError(
  err: unknown,
  ctx: ErrorContext,
  options?: ReportOptions
): Promise<void> {
  try {
    const { name, message, stack } = safeStringify(err);
    const frame = topFrame(stack);
    const fingerprintInput =
      options?.fingerprintOverride ??
      `${name}:${frame}:${ctx.surface}:${ctx.screen ?? ""}`;
    const fingerprint = await sha256Hex(fingerprintInput);

    // Only attempt the Firestore write if the SDK is initialized on this
    // surface. Cloud Functions imports this module without setting up the
    // client SDK — there, the surface-specific functions/src/lib/errorReporter
    // shim writes via admin SDK instead.
    if (!isFirebaseReady()) {
      return;
    }

    const doc: Record<string, unknown> = {
      fingerprint,
      surface: ctx.surface,
      source: ctx.source ?? "auto",
      name,
      message,
      stack,
      topFrame: frame,
      breadcrumbs: getBreadcrumbs(),
      createdAt: serverTimestamp(),
      triageStatus: "pending",
      environment: options?.environment ?? "production",
    };

    // Conditionally add optional fields — Firestore rejects undefined values.
    if (ctx.userId) doc.userId = ctx.userId;
    if (ctx.reporterProfileId) doc.reporterProfileId = ctx.reporterProfileId;
    if (ctx.userRole) doc.userRole = ctx.userRole;
    if (typeof ctx.isDesignPartner === "boolean") doc.isDesignPartner = ctx.isDesignPartner;
    if (ctx.screen) doc.screen = ctx.screen;
    if (ctx.buildVersion) doc.buildVersion = ctx.buildVersion;
    if (ctx.extra) doc.extra = ctx.extra;
    if (ctx.description) doc.description = ctx.description;
    if (ctx.expected) doc.expected = ctx.expected;
    if (ctx.actual) doc.actual = ctx.actual;
    if (ctx.severityHint) doc.severityHint = ctx.severityHint;
    if (ctx.screenshotPath) doc.screenshotPath = ctx.screenshotPath;

    // Firestore rejects `undefined` at ANY depth, and it throws SYNCHRONOUSLY
    // from addDoc() (not as a rejected promise). Nested optionals slip past the
    // top-level guards above — e.g. a breadcrumb's `data` map or an `extra`
    // value can hold `undefined` — so deep-strip before writing. stripUndefined
    // preserves the serverTimestamp() FieldValue sentinel (via `_methodName`).
    const cleanDoc = stripUndefined(doc);

    // Fire-and-forget. The Firestore SDK queues writes; we don't await. Wrap in
    // its own try so a synchronous addDoc() validation throw can never cascade
    // out of the reporter and crash the app on top of the error it is reporting.
    try {
      addDoc(collection(getDb(), "errorEvents"), cleanDoc).catch((writeErr) => {
        // Never crash the app on a reporting failure — but don't swallow it
        // silently anymore. Forward to the registered sink (Sentry on mobile) so a
        // broken write path is visible instead of invisible.
        try {
          // eslint-disable-next-line no-console
          console.warn("[errorReporter] errorEvents write failed:", writeErr);
          writeErrorSink?.(writeErr);
        } catch {
          // give up — reporting must never throw
        }
      });
    } catch (syncWriteErr) {
      try {
        // eslint-disable-next-line no-console
        console.warn("[errorReporter] errorEvents write threw synchronously:", syncWriteErr);
        writeErrorSink?.(syncWriteErr);
      } catch {
        // give up — reporting must never throw
      }
    }
  } catch (loopGuard) {
    try {
      // eslint-disable-next-line no-console
      console.error("[errorReporter] reporter itself threw — swallowing:", loopGuard);
    } catch {
      // give up
    }
  }
}
